import { useState } from 'react'
import { Plus, Trash2, Edit2, Settings, AlertCircle, CheckCircle, Bell, GitBranch, Terminal, Key, HardDrive, Users, Mail, Play, Copy, Eye, EyeOff, Server, Shield, BarChart3, Save, X, Github, BrainCircuit, Globe, Lock } from 'lucide-react'

export default function AISystemAdminConsole() {
  const [activeModule, setActiveModule] = useState('notifications')
  const [showAddForm, setShowAddForm] = useState(false)

  // Notification Center State
  const [notifications, setNotifications] = useState([
    { id: 1, name: 'CPU Alert', rule: 'CPU > 80%', channel: 'Email, Slack', status: 'Active', lastTriggered: '5 mins ago' },
    { id: 2, name: 'Deployment Failed', rule: 'Build Status = Failed', channel: 'Email, Discord', status: 'Active', lastTriggered: '2 hours ago' },
  ])

  // CI/CD Pipeline State
  const [pipelines, setPipelines] = useState([
    { id: 1, name: 'Main Repo', platform: 'GitHub', repo: 'cybertection/main', branch: 'main', status: 'Running', lastBuild: '2 mins ago', commits: 12 },
    { id: 2, name: 'Dev Branch', platform: 'GitLab', repo: 'cybertection/dev', branch: 'develop', status: 'Success', lastBuild: '15 mins ago', commits: 8 },
  ])

  // Remote Server State
  const [servers, setServers] = useState([
    { id: 1, name: 'Production Server', ip: '192.168.1.100', port: 22, status: 'Connected', os: 'Ubuntu 22.04', uptime: '45 days' },
    { id: 2, name: 'Backup Server', ip: '192.168.1.101', port: 22, status: 'Connected', os: 'CentOS 8', uptime: '32 days' },
  ])

  // Environment & Secrets State
  const [environments, setEnvironments] = useState([
    { id: 1, name: 'Production', type: 'prod', variables: 12, secrets: 8, status: 'Active' },
    { id: 2, name: 'Staging', type: 'staging', variables: 10, secrets: 8, status: 'Active' },
    { id: 3, name: 'Development', type: 'dev', variables: 15, secrets: 12, status: 'Active' },
  ])

  // Backup & Recovery State
  const [backups, setBackups] = useState([
    { id: 1, name: 'Daily Full Backup', schedule: '0 2 * * *', lastRun: '2024-02-19 02:00 AM', size: '45 GB', status: 'Success' },
    { id: 2, name: 'Hourly Incremental', schedule: '0 * * * *', lastRun: '2024-02-19 03:00 AM', size: '2.3 GB', status: 'Success' },
  ])

  // System Monitoring State
  const [systemMetrics, setSystemMetrics] = useState({
    cpu: 34,
    memory: 62,
    disk: 78,
    uptime: '45 days 12 hours',
    processes: 156,
    connections: 48,
  })

  // Users & Roles State
  const [users, setUsers] = useState([
    { id: 1, name: 'Admin User', email: 'admin@cybertection.com', role: 'Super Admin', status: 'Active', lastLogin: '1 min ago' },
    { id: 2, name: 'AI Agent', email: 'ai-agent@cybertection.com', role: 'Admin', status: 'Active', lastLogin: '30 secs ago' },
    { id: 3, name: 'Developer', email: 'dev@cybertection.com', role: 'Editor', status: 'Active', lastLogin: '2 hours ago' },
  ])

  // Email & Messaging State
  const [emailTemplates, setEmailTemplates] = useState([
    { id: 1, name: 'Alert Notification', type: 'Email', status: 'Active', recipients: 5 },
    { id: 2, name: 'Deployment Success', type: 'Email', status: 'Active', recipients: 8 },
    { id: 3, name: 'System Alert', type: 'SMS', status: 'Active', recipients: 3 },
  ])

  // GitHub Integration State
  const [githubAccounts, setGithubAccounts] = useState([
    { id: 1, username: 'cybertection-dev', token: 'ghp_****...***', status: 'Connected', repos: 12, lastSync: '5 mins ago', url: 'https://github.com/cybertection-dev' },
    { id: 2, username: 'company-org', token: 'ghp_****...***', status: 'Connected', repos: 28, lastSync: '15 mins ago', url: 'https://github.com/company-org' },
  ])

  const [githubRepos, setGithubRepos] = useState([
    { id: 1, name: 'cybertection-main', owner: 'cybertection-dev', url: 'https://github.com/cybertection-dev/cybertection-main', description: 'Main application repository', language: 'TypeScript', stars: 156, watchers: 42, forks: 18, branches: 5, lastUpdate: '2 hours ago' },
    { id: 2, name: 'ai-helpdesk', owner: 'cybertection-dev', url: 'https://github.com/cybertection-dev/ai-helpdesk', description: 'AI-powered help desk system', language: 'JavaScript', stars: 89, watchers: 24, forks: 12, branches: 3, lastUpdate: '30 mins ago' },
    { id: 3, name: 'security-audit-tool', owner: 'company-org', url: 'https://github.com/company-org/security-audit-tool', description: 'Enterprise security auditing', language: 'Python', stars: 234, watchers: 67, forks: 31, branches: 8, lastUpdate: '1 day ago' },
  ])

  const [showGithubForm, setShowGithubForm] = useState(false)
  const [githubFormData, setGithubFormData] = useState({ username: '', token: '' })

  // Hugging Face Integration State
  const [huggingFaceAccounts, setHuggingFaceAccounts] = useState([
    { id: 1, username: 'cybertection-ai', token: 'hf_****...***', status: 'Connected', models: 8, datasets: 5, lastSync: '10 mins ago', url: 'https://huggingface.co/cybertection-ai' },
    { id: 2, username: 'ai-research-team', token: 'hf_****...***', status: 'Connected', models: 24, datasets: 12, lastSync: '20 mins ago', url: 'https://huggingface.co/ai-research-team' },
  ])

  const [huggingFaceModels, setHuggingFaceModels] = useState([
    { id: 1, name: 'distilbert-base-uncased', owner: 'cybertection-ai', url: 'https://huggingface.co/cybertection-ai/distilbert-base-uncased', description: 'Fine-tuned BERT model for text classification', task: 'Text Classification', framework: 'PyTorch', downloads: 15420, likes: 342, lastUpdate: '3 days ago' },
    { id: 2, name: 'gpt2-medium-finetuned', owner: 'cybertection-ai', url: 'https://huggingface.co/cybertection-ai/gpt2-medium-finetuned', description: 'GPT-2 medium model fine-tuned for dialogue', task: 'Text Generation', framework: 'TensorFlow', downloads: 8930, likes: 287, lastUpdate: '1 week ago' },
    { id: 3, name: 'roberta-sentiment-analysis', owner: 'ai-research-team', url: 'https://huggingface.co/ai-research-team/roberta-sentiment-analysis', description: 'RoBERTa model for sentiment analysis', task: 'Sentiment Analysis', framework: 'PyTorch', downloads: 42150, likes: 891, lastUpdate: '2 days ago' },
    { id: 4, name: 'multi-lingual-translator', owner: 'ai-research-team', url: 'https://huggingface.co/ai-research-team/multi-lingual-translator', description: 'Multilingual translation model', task: 'Translation', framework: 'PyTorch', downloads: 23670, likes: 534, lastUpdate: '5 days ago' },
  ])

  const [showHuggingFaceForm, setShowHuggingFaceForm] = useState(false)
  const [huggingFaceFormData, setHuggingFaceFormData] = useState({ username: '', token: '' })

  // Website Dev State
  const [websites, setWebsites] = useState([
    { id: 1, domain: 'cybertection.com', url: 'https://cybertection.com', status: 'Live', frameworks: ['React', 'Node.js', 'MongoDB'], dns: 'Route53', cert: 'Let\'s Encrypt (Valid until 2026-05-20)', host: 'AWS CloudFront', traffic: '156K/month', lastUpdated: '2 days ago' },
    { id: 2, domain: 'ai-helpdesk.cybertection.com', url: 'https://ai-helpdesk.cybertection.com', status: 'Live', frameworks: ['React 18', 'Express.js', 'PostgreSQL'], dns: 'CloudFlare', cert: 'Digicert SSL (Valid until 2025-12-15)', host: 'Heroku', traffic: '89K/month', lastUpdated: '5 hours ago' },
    { id: 3, domain: 'dev.cybertection.com', url: 'https://dev.cybertection.com', status: 'Staging', frameworks: ['Vue.js', 'Django', 'PostgreSQL'], dns: 'Namecheap', cert: 'Self-signed', host: 'DigitalOcean', traffic: '12K/month', lastUpdated: '1 day ago' },
    { id: 4, domain: 'blog.cybertection.com', url: 'https://blog.cybertection.com', status: 'Live', frameworks: ['Next.js', 'Sanity CMS'], dns: 'Vercel', cert: 'Let\'s Encrypt (Valid until 2026-03-10)', host: 'Vercel', traffic: '234K/month', lastUpdated: '30 mins ago' },
  ])

  const [showWebsiteForm, setShowWebsiteForm] = useState(false)
  const [websiteFormData, setWebsiteFormData] = useState({ domain: '', url: '', frameworks: '', dns: '', cert: '', host: '' })

  const modules = [
    { id: 'notifications', icon: Bell, label: 'Notifications', color: 'from-blue-600' },
    { id: 'pipelines', icon: GitBranch, label: 'CI/CD Pipelines', color: 'from-purple-600' },
    { id: 'website', icon: Globe, label: 'Website Dev', color: 'from-teal-600' },
    { id: 'github', icon: Github, label: 'GitHub', color: 'from-slate-600' },
    { id: 'huggingface', icon: BrainCircuit, label: 'Hugging Face', color: 'from-yellow-600' },
    { id: 'servers', icon: Terminal, label: 'Remote Servers', color: 'from-green-600' },
    { id: 'environment', icon: Key, label: 'Environment & Secrets', color: 'from-orange-600' },
    { id: 'backups', icon: HardDrive, label: 'Backup & Recovery', color: 'from-red-600' },
    { id: 'monitoring', icon: BarChart3, label: 'System Monitoring', color: 'from-cyan-600' },
    { id: 'users', icon: Users, label: 'User & Roles', color: 'from-pink-600' },
    { id: 'email', icon: Mail, label: 'Email & Messaging', color: 'from-indigo-600' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">AI System Admin Console</h1>
        <p className="text-blue-200">Complete system control and management for AI agents and automation</p>
      </div>

      {/* Module Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {modules.map(module => {
          const Icon = module.icon
          return (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                activeModule === module.id
                  ? `bg-gradient-to-br ${module.color}/40 border-blue-500 shadow-lg shadow-blue-500/30`
                  : 'bg-slate-800/50 border-blue-500/20 hover:border-blue-500/40'
              }`}
            >
              <Icon size={24} className="text-blue-400 mb-2" />
              <p className="text-sm font-semibold text-white">{module.label}</p>
            </button>
          )
        })}
      </div>

      {/* Notifications Module */}
      {activeModule === 'notifications' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Notification Center</h2>
            <p className="text-blue-200 mb-6">Configure alert rules, notification templates, and distribution channels</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
                <p className="text-blue-300/70 text-sm mb-2">Active Alerts</p>
                <p className="text-3xl font-bold text-blue-400">{notifications.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
                <p className="text-blue-300/70 text-sm mb-2">Channels</p>
                <p className="text-3xl font-bold text-blue-400">4</p>
                <p className="text-xs text-blue-300 mt-2">Email, Slack, Discord, SMS</p>
              </div>
            </div>

            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all flex items-center justify-center space-x-2 mb-6"
              >
                <Plus size={20} />
                <span>Add Alert Rule</span>
              </button>
            ) : (
              <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20 mb-6 space-y-4">
                <input type="text" placeholder="Alert Name" className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50" />
                <input type="text" placeholder="Alert Rule (e.g., CPU > 80%)" className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50" />
                <select className="w-full px-4 py-2 bg-slate-700/50 border border-blue-500/30 rounded-lg text-white">
                  <option>Select Distribution Channel...</option>
                  <option>Email</option>
                  <option>Slack</option>
                  <option>Discord</option>
                  <option>SMS</option>
                  <option>All Channels</option>
                </select>
                <div className="flex gap-3">
                  <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Save Rule</button>
                  <button onClick={() => setShowAddForm(false)} className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-semibold">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {notifications.map(alert => (
                <div key={alert.id} className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-white">{alert.name}</p>
                      <p className="text-sm text-blue-300 mt-1">{alert.rule}</p>
                      <p className="text-xs text-blue-300/70 mt-1">Channels: {alert.channel}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{alert.status}</span>
                  </div>
                  <p className="text-xs text-blue-300 mt-3">Last triggered: {alert.lastTriggered}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CI/CD Pipelines Module */}
      {activeModule === 'pipelines' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">CI/CD Pipeline Management</h2>
            <p className="text-blue-200 mb-6">Connect and manage GitHub Actions, GitLab CI, and view/modify code</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                <p className="text-purple-300/70 text-sm mb-2">Connected Pipelines</p>
                <p className="text-3xl font-bold text-purple-400">{pipelines.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                <p className="text-purple-300/70 text-sm mb-2">Active Deployments</p>
                <p className="text-3xl font-bold text-purple-400">1</p>
              </div>
            </div>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Connect GitHub/GitLab Repository</span>
            </button>

            <div className="space-y-3">
              {pipelines.map(pipeline => (
                <div key={pipeline.id} className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{pipeline.name}</p>
                      <p className="text-sm text-purple-300 mt-1">{pipeline.platform}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${pipeline.status === 'Running' ? 'bg-yellow-500/30 text-yellow-300' : 'bg-green-500/30 text-green-300'}`}>{pipeline.status}</span>
                  </div>
                  <p className="text-xs text-purple-300 mb-3">Repository: {pipeline.repo} • Branch: {pipeline.branch}</p>
                  <div className="flex gap-2 justify-between text-xs text-purple-300">
                    <span>Last Build: {pipeline.lastBuild}</span>
                    <span>Commits: {pipeline.commits}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex items-center justify-center space-x-1">
                      <Eye size={14} />
                      <span>View Code</span>
                    </button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 flex items-center justify-center space-x-1">
                      <Edit2 size={14} />
                      <span>Modify</span>
                    </button>
                    <button className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center justify-center space-x-1">
                      <Play size={14} />
                      <span>Trigger</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Remote Servers Module */}
      {activeModule === 'servers' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Remote Server Control</h2>
            <p className="text-blue-200 mb-6">SSH access, command execution, and server management</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-green-500/20">
                <p className="text-green-300/70 text-sm mb-2">Connected Servers</p>
                <p className="text-3xl font-bold text-green-400">{servers.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-green-500/20">
                <p className="text-green-300/70 text-sm mb-2">All Systems</p>
                <p className="text-3xl font-bold text-green-400">Healthy</p>
              </div>
            </div>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Add Server Connection</span>
            </button>

            <div className="space-y-3">
              {servers.map(server => (
                <div key={server.id} className="bg-slate-800/50 rounded-lg p-4 border border-green-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{server.name}</p>
                      <p className="text-sm text-green-300 mt-1">{server.ip}:{server.port}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{server.status}</span>
                  </div>
                  <p className="text-xs text-green-300 mb-3">OS: {server.os} • Uptime: {server.uptime}</p>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700 font-semibold flex items-center justify-center space-x-1">
                      <Terminal size={14} />
                      <span>SSH Terminal</span>
                    </button>
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-semibold">Execute Command</button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 font-semibold">Deploy</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Environment & Secrets Module */}
      {activeModule === 'environment' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Environment & Secrets Management</h2>
            <p className="text-blue-200 mb-6">Manage variables, secrets, and configurations for all services</p>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-orange-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Add Environment Configuration</span>
            </button>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {environments.map(env => (
                <div key={env.id} className="bg-slate-800/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-bold text-white">{env.name}</p>
                      <p className="text-xs text-orange-300 mt-1">Type: {env.type}</p>
                    </div>
                    <span className="px-2 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded">{env.status}</span>
                  </div>
                  <div className="space-y-2 text-xs text-orange-200 mb-3">
                    <p>Variables: {env.variables}</p>
                    <p>Secrets: {env.secrets}</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex items-center justify-center space-x-1">
                      <Edit2 size={12} />
                      <span>Edit</span>
                    </button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 flex items-center justify-center space-x-1">
                      <Key size={12} />
                      <span>Secrets</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backup & Recovery Module */}
      {activeModule === 'backups' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-lg border border-red-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Backup & Recovery</h2>
            <p className="text-blue-200 mb-6">Schedule backups, manage versions, and disaster recovery</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-red-500/20">
                <p className="text-red-300/70 text-sm mb-2">Active Backups</p>
                <p className="text-3xl font-bold text-red-400">{backups.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-red-500/20">
                <p className="text-red-300/70 text-sm mb-2">Total Backup Size</p>
                <p className="text-3xl font-bold text-red-400">47.3 GB</p>
              </div>
            </div>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-red-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Schedule New Backup</span>
            </button>

            <div className="space-y-3">
              {backups.map(backup => (
                <div key={backup.id} className="bg-slate-800/50 rounded-lg p-4 border border-red-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{backup.name}</p>
                      <p className="text-sm text-red-300 mt-1">Schedule: {backup.schedule}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{backup.status}</span>
                  </div>
                  <p className="text-xs text-red-300 mb-3">Last Run: {backup.lastRun} • Size: {backup.size}</p>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">View History</button>
                    <button className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700">Restore</button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700">Run Now</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* System Monitoring Module */}
      {activeModule === 'monitoring' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-cyan-600/20 to-blue-600/20 rounded-lg border border-cyan-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">System Monitoring & Logs</h2>
            <p className="text-blue-200 mb-6">Real-time monitoring, logs, error tracking, and performance metrics</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20">
                <p className="text-cyan-300/70 text-sm mb-2">CPU Usage</p>
                <p className="text-3xl font-bold text-cyan-400">{systemMetrics.cpu}%</p>
                <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                  <div className="bg-cyan-500 h-2 rounded-full" style={{ width: `${systemMetrics.cpu}%` }}></div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20">
                <p className="text-cyan-300/70 text-sm mb-2">Memory Usage</p>
                <p className="text-3xl font-bold text-cyan-400">{systemMetrics.memory}%</p>
                <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                  <div className="bg-cyan-500 h-2 rounded-full" style={{ width: `${systemMetrics.memory}%` }}></div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20">
                <p className="text-cyan-300/70 text-sm mb-2">Disk Usage</p>
                <p className="text-3xl font-bold text-cyan-400">{systemMetrics.disk}%</p>
                <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                  <div className="bg-cyan-500 h-2 rounded-full" style={{ width: `${systemMetrics.disk}%` }}></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20 text-center">
                <p className="text-cyan-300/70 text-sm mb-1">System Uptime</p>
                <p className="text-xl font-bold text-cyan-400">{systemMetrics.uptime}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20 text-center">
                <p className="text-cyan-300/70 text-sm mb-1">Active Processes</p>
                <p className="text-xl font-bold text-cyan-400">{systemMetrics.processes}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-500/20 text-center">
                <p className="text-cyan-300/70 text-sm mb-1">Connections</p>
                <p className="text-xl font-bold text-cyan-400">{systemMetrics.connections}</p>
              </div>
            </div>

            <div className="flex gap-4">
              <button className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center space-x-2">
                <Eye size={20} />
                <span>View Logs</span>
              </button>
              <button className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 flex items-center justify-center space-x-2">
                <BarChart3 size={20} />
                <span>Analytics</span>
              </button>
              <button className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 flex items-center justify-center space-x-2">
                <AlertCircle size={20} />
                <span>Errors</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users & Roles Module */}
      {activeModule === 'users' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-pink-600/20 to-rose-600/20 rounded-lg border border-pink-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">User & Role Management</h2>
            <p className="text-blue-200 mb-6">Create users, assign roles, manage permissions, and track activity</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-pink-500/20">
                <p className="text-pink-300/70 text-sm mb-2">Total Users</p>
                <p className="text-3xl font-bold text-pink-400">{users.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-pink-500/20">
                <p className="text-pink-300/70 text-sm mb-2">Active Now</p>
                <p className="text-3xl font-bold text-pink-400">{users.filter(u => u.status === 'Active').length}</p>
              </div>
            </div>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-pink-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Add New User</span>
            </button>

            <div className="space-y-3">
              {users.map(user => (
                <div key={user.id} className="bg-slate-800/50 rounded-lg p-4 border border-pink-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{user.name}</p>
                      <p className="text-sm text-pink-300 mt-1">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <span className="px-3 py-1 bg-blue-500/30 text-blue-300 text-xs font-bold rounded-full block mb-2">{user.role}</span>
                      <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{user.status}</span>
                    </div>
                  </div>
                  <p className="text-xs text-pink-300 mb-3">Last Login: {user.lastLogin}</p>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-semibold">Edit Role</button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 font-semibold">Permissions</button>
                    <button className="flex-1 px-3 py-2 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 font-semibold">Activity</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Email & Messaging Module */}
      {activeModule === 'email' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-lg border border-indigo-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Email & Messaging System</h2>
            <p className="text-blue-200 mb-6">Send emails, SMS, push notifications, and manage templates</p>

            <button className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-indigo-500/50 transition-all flex items-center justify-center space-x-2 mb-6">
              <Plus size={20} />
              <span>Create Message Template</span>
            </button>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-indigo-500/20 text-center">
                <p className="text-indigo-300/70 text-sm mb-1">Email Templates</p>
                <p className="text-2xl font-bold text-indigo-400">2</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-indigo-500/20 text-center">
                <p className="text-indigo-300/70 text-sm mb-1">SMS Templates</p>
                <p className="text-2xl font-bold text-indigo-400">1</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-indigo-500/20 text-center">
                <p className="text-indigo-300/70 text-sm mb-1">Push Notifications</p>
                <p className="text-2xl font-bold text-indigo-400">Active</p>
              </div>
            </div>

            <div className="space-y-3">
              {emailTemplates.map(template => (
                <div key={template.id} className="bg-slate-800/50 rounded-lg p-4 border border-indigo-500/20">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-bold text-white">{template.name}</p>
                      <p className="text-sm text-indigo-300 mt-1">Type: {template.type}</p>
                    </div>
                    <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{template.status}</span>
                  </div>
                  <p className="text-xs text-indigo-300 mb-3">Recipients: {template.recipients}</p>
                  <div className="flex gap-2">
                    <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-semibold">Edit</button>
                    <button className="flex-1 px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700 font-semibold">Send Test</button>
                    <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 font-semibold">Preview</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GitHub Module */}
      {activeModule === 'github' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-600/20 to-gray-600/20 rounded-lg border border-slate-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">GitHub Integration</h2>
            <p className="text-blue-200 mb-6">Connect and manage GitHub account, repositories and automation.</p>

            {/* GitHub Accounts Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Github size={20} />
                Connected GitHub Accounts
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-500/20">
                  <p className="text-slate-300/70 text-sm mb-2">Total Accounts</p>
                  <p className="text-3xl font-bold text-slate-400">{githubAccounts.length}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-500/20">
                  <p className="text-slate-300/70 text-sm mb-2">Total Repositories</p>
                  <p className="text-3xl font-bold text-slate-400">{githubRepos.length}</p>
                </div>
              </div>

              {!showGithubForm ? (
                <button
                  onClick={() => setShowGithubForm(true)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-slate-500/50 transition-all flex items-center justify-center space-x-2 mb-6"
                >
                  <Plus size={20} />
                  <span>Connect GitHub Account</span>
                </button>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-500/20 mb-6 space-y-4">
                  <h4 className="text-white font-semibold">Connect New GitHub Account</h4>
                  <input
                    type="text"
                    placeholder="GitHub Username"
                    value={githubFormData.username}
                    onChange={(e) => setGithubFormData({...githubFormData, username: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-500/30 rounded-lg text-white placeholder-slate-400"
                  />
                  <div>
                    <label className="text-slate-300 text-sm mb-2 block">Personal Access Token</label>
                    <input
                      type="password"
                      placeholder="ghp_xxxxxxxxxxxxx"
                      value={githubFormData.token}
                      onChange={(e) => setGithubFormData({...githubFormData, token: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700/50 border border-slate-500/30 rounded-lg text-white placeholder-slate-400"
                    />
                    <p className="text-xs text-slate-400 mt-2">Get token from: https://github.com/settings/tokens</p>
                  </div>
                  <div className="flex gap-3">
                    <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Connect Account</button>
                    <button onClick={() => setShowGithubForm(false)} className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-semibold">Cancel</button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {githubAccounts.map(account => (
                  <div key={account.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-500/20">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-bold text-white flex items-center gap-2">
                          <Github size={18} className="text-slate-400" />
                          {account.username}
                        </p>
                        <p className="text-xs text-slate-400 mt-2 font-mono break-all">{account.token}</p>
                      </div>
                      <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{account.status}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3 text-xs text-slate-300">
                      <div>📦 {account.repos} Repos</div>
                      <div>🔄 Synced {account.lastSync}</div>
                      <a href={account.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">View Profile →</a>
                    </div>
                    <div className="flex gap-2">
                      <button className="flex-1 px-3 py-2 bg-blue-600/50 text-blue-300 rounded text-xs hover:bg-blue-600 font-semibold">Sync Repos</button>
                      <button className="flex-1 px-3 py-2 bg-slate-600/50 text-slate-300 rounded text-xs hover:bg-slate-600 font-semibold">Edit</button>
                      <button className="flex-1 px-3 py-2 bg-red-600/50 text-red-300 rounded text-xs hover:bg-red-600 font-semibold">Disconnect</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* GitHub Repositories Section */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <GitBranch size={20} />
                GitHub Repositories
              </h3>

              <div className="space-y-3">
                {githubRepos.map(repo => (
                  <div key={repo.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-500/20 hover:border-slate-500/40 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <a href={repo.url} target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-blue-400 hover:text-blue-300 flex items-center gap-2">
                          <Github size={18} />
                          {repo.owner}/{repo.name}
                        </a>
                        <p className="text-sm text-slate-400 mt-1">{repo.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3 text-xs">
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">Language:</span>
                        <p className="text-slate-200 font-semibold">{repo.language}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">⭐ Stars:</span>
                        <p className="text-slate-200 font-semibold">{repo.stars}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">👀 Watchers:</span>
                        <p className="text-slate-200 font-semibold">{repo.watchers}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">🍴 Forks:</span>
                        <p className="text-slate-200 font-semibold">{repo.forks}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">🌿 Branches:</span>
                        <p className="text-slate-200 font-semibold">{repo.branches}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">Updated:</span>
                        <p className="text-slate-200 font-semibold">{repo.lastUpdate}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button className="flex-1 px-3 py-2 bg-blue-600/50 text-blue-300 rounded text-xs hover:bg-blue-600 font-semibold flex items-center justify-center gap-1">
                        <Eye size={14} />
                        View Repo
                      </button>
                      <button className="flex-1 px-3 py-2 bg-purple-600/50 text-purple-300 rounded text-xs hover:bg-purple-600 font-semibold flex items-center justify-center gap-1">
                        <GitBranch size={14} />
                        Clone URL
                      </button>
                      <button className="flex-1 px-3 py-2 bg-green-600/50 text-green-300 rounded text-xs hover:bg-green-600 font-semibold flex items-center justify-center gap-1">
                        <Copy size={14} />
                        Copy
                      </button>
                      <button className="flex-1 px-3 py-2 bg-slate-600/50 text-slate-300 rounded text-xs hover:bg-slate-600 font-semibold">Manage</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Website Dev Module */}
      {activeModule === 'website' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-teal-600/20 to-cyan-600/20 rounded-lg border border-teal-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Website Development Management</h2>
            <p className="text-blue-200 mb-6">Manage all company websites, frameworks, DNS, and SSL certificates in one place.</p>

            {/* Website Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-teal-500/20">
                <p className="text-teal-300/70 text-sm mb-2">Total Websites</p>
                <p className="text-3xl font-bold text-teal-400">{websites.length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-teal-500/20">
                <p className="text-teal-300/70 text-sm mb-2">Live Websites</p>
                <p className="text-3xl font-bold text-teal-400">{websites.filter(w => w.status === 'Live').length}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-teal-500/20">
                <p className="text-teal-300/70 text-sm mb-2">Total Traffic</p>
                <p className="text-3xl font-bold text-teal-400">{websites.reduce((sum, w) => sum + parseInt(w.traffic), 0).toLocaleString()}K</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-teal-500/20">
                <p className="text-teal-300/70 text-sm mb-2">Unique Frameworks</p>
                <p className="text-3xl font-bold text-teal-400">{new Set(websites.flatMap(w => w.frameworks)).size}</p>
              </div>
            </div>

            {/* Add Website Button */}
            {!showWebsiteForm ? (
              <button
                onClick={() => setShowWebsiteForm(true)}
                className="w-full px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-teal-500/50 transition-all flex items-center justify-center space-x-2 mb-6"
              >
                <Plus size={20} />
                <span>Add New Website</span>
              </button>
            ) : (
              <div className="bg-slate-800/50 rounded-lg p-6 border border-teal-500/20 mb-6 space-y-4">
                <h4 className="text-white font-semibold">Add New Website</h4>
                <input
                  type="text"
                  placeholder="Domain (e.g., example.com)"
                  value={websiteFormData.domain}
                  onChange={(e) => setWebsiteFormData({...websiteFormData, domain: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                />
                <input
                  type="text"
                  placeholder="Website URL (e.g., https://example.com)"
                  value={websiteFormData.url}
                  onChange={(e) => setWebsiteFormData({...websiteFormData, url: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                />
                <input
                  type="text"
                  placeholder="Frameworks (e.g., React, Node.js, MongoDB)"
                  value={websiteFormData.frameworks}
                  onChange={(e) => setWebsiteFormData({...websiteFormData, frameworks: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="DNS Provider (e.g., Route53)"
                    value={websiteFormData.dns}
                    onChange={(e) => setWebsiteFormData({...websiteFormData, dns: e.target.value})}
                    className="px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Hosting (e.g., AWS)"
                    value={websiteFormData.host}
                    onChange={(e) => setWebsiteFormData({...websiteFormData, host: e.target.value})}
                    className="px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                  />
                </div>
                <input
                  type="text"
                  placeholder="SSL Certificate (e.g., Let's Encrypt)"
                  value={websiteFormData.cert}
                  onChange={(e) => setWebsiteFormData({...websiteFormData, cert: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-700/50 border border-teal-500/30 rounded-lg text-white placeholder-slate-400"
                />
                <div className="flex gap-3">
                  <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Add Website</button>
                  <button onClick={() => setShowWebsiteForm(false)} className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-semibold">Cancel</button>
                </div>
              </div>
            )}

            {/* Websites List */}
            <div className="space-y-4">
              {websites.map(website => (
                <div key={website.id} className="bg-slate-800/50 rounded-lg p-6 border border-teal-500/20 hover:border-teal-500/40 transition-all">
                  {/* Domain Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <a href={website.url} target="_blank" rel="noopener noreferrer" className="text-xl font-bold text-teal-400 hover:text-teal-300 flex items-center gap-2">
                        <Globe size={22} />
                        {website.domain}
                      </a>
                      <p className="text-sm text-teal-300 mt-2">{website.url}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${website.status === 'Live' ? 'bg-green-500/30 text-green-300' : 'bg-yellow-500/30 text-yellow-300'}`}>
                      {website.status}
                    </span>
                  </div>

                  {/* Frameworks */}
                  <div className="mb-4">
                    <p className="text-teal-300 text-sm font-semibold mb-2">🛠️ Frameworks & Stack:</p>
                    <div className="flex flex-wrap gap-2">
                      {website.frameworks.map((framework, idx) => (
                        <span key={idx} className="px-3 py-1 bg-teal-600/30 text-teal-300 text-xs rounded-full border border-teal-500/30">
                          {framework}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* DNS, Cert, and Hosting */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="bg-slate-700/30 rounded-lg p-3 border border-teal-500/10">
                      <p className="text-teal-300/70 text-xs font-semibold">🌐 DNS Provider</p>
                      <p className="text-white font-semibold mt-1">{website.dns}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3 border border-teal-500/10">
                      <p className="text-teal-300/70 text-xs font-semibold">☁️ Hosting</p>
                      <p className="text-white font-semibold mt-1">{website.host}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3 border border-teal-500/10">
                      <p className="text-teal-300/70 text-xs font-semibold">🔒 SSL Certificate</p>
                      <p className="text-white font-semibold text-xs mt-1">{website.cert}</p>
                    </div>
                  </div>

                  {/* Traffic and Last Updated */}
                  <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                    <div className="bg-slate-700/30 rounded-lg px-3 py-2">
                      <span className="text-slate-400">📊 Monthly Traffic:</span>
                      <p className="text-slate-200 font-semibold">{website.traffic}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg px-3 py-2">
                      <span className="text-slate-400">🕐 Last Updated:</span>
                      <p className="text-slate-200 font-semibold">{website.lastUpdated}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <button className="flex-1 px-3 py-2 bg-blue-600/50 text-blue-300 rounded text-xs hover:bg-blue-600 font-semibold flex items-center justify-center gap-1">
                      <Eye size={14} />
                      Visit
                    </button>
                    <button className="flex-1 px-3 py-2 bg-green-600/50 text-green-300 rounded text-xs hover:bg-green-600 font-semibold flex items-center justify-center gap-1">
                      <Lock size={14} />
                      SSL Status
                    </button>
                    <button className="flex-1 px-3 py-2 bg-teal-600/50 text-teal-300 rounded text-xs hover:bg-teal-600 font-semibold flex items-center justify-center gap-1">
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button className="flex-1 px-3 py-2 bg-red-600/50 text-red-300 rounded text-xs hover:bg-red-600 font-semibold flex items-center justify-center gap-1">
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hugging Face Module */}
      {activeModule === 'huggingface' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-lg border border-yellow-500/30 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Hugging Face Integration</h2>
            <p className="text-blue-200 mb-6">Monitor models, track usage metrics, and manage AI deployments.</p>

            {/* Hugging Face Accounts Section */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <BrainCircuit size={20} />
                Connected Hugging Face Accounts
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-yellow-500/20">
                  <p className="text-yellow-300/70 text-sm mb-2">Total Accounts</p>
                  <p className="text-3xl font-bold text-yellow-400">{huggingFaceAccounts.length}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-yellow-500/20">
                  <p className="text-yellow-300/70 text-sm mb-2">Total Models</p>
                  <p className="text-3xl font-bold text-yellow-400">{huggingFaceModels.length}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-yellow-500/20">
                  <p className="text-yellow-300/70 text-sm mb-2">Combined Datasets</p>
                  <p className="text-3xl font-bold text-yellow-400">{huggingFaceAccounts.reduce((sum, acc) => sum + acc.datasets, 0)}</p>
                </div>
              </div>

              {!showHuggingFaceForm ? (
                <button
                  onClick={() => setShowHuggingFaceForm(true)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-yellow-700 to-orange-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-yellow-500/50 transition-all flex items-center justify-center space-x-2 mb-6"
                >
                  <Plus size={20} />
                  <span>Connect Hugging Face Account</span>
                </button>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-6 border border-yellow-500/20 mb-6 space-y-4">
                  <h4 className="text-white font-semibold">Connect New Hugging Face Account</h4>
                  <input
                    type="text"
                    placeholder="Hugging Face Username"
                    value={huggingFaceFormData.username}
                    onChange={(e) => setHuggingFaceFormData({...huggingFaceFormData, username: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-yellow-500/30 rounded-lg text-white placeholder-slate-400"
                  />
                  <div>
                    <label className="text-yellow-300 text-sm mb-2 block">Hugging Face API Token</label>
                    <input
                      type="password"
                      placeholder="hf_xxxxxxxxxxxxx"
                      value={huggingFaceFormData.token}
                      onChange={(e) => setHuggingFaceFormData({...huggingFaceFormData, token: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700/50 border border-yellow-500/30 rounded-lg text-white placeholder-slate-400"
                    />
                    <p className="text-xs text-yellow-300 mt-2">Get token from: https://huggingface.co/settings/tokens</p>
                  </div>
                  <div className="flex gap-3">
                    <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700">Connect Account</button>
                    <button onClick={() => setShowHuggingFaceForm(false)} className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-semibold">Cancel</button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {huggingFaceAccounts.map(account => (
                  <div key={account.id} className="bg-slate-800/50 rounded-lg p-4 border border-yellow-500/20">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-bold text-white flex items-center gap-2">
                          <BrainCircuit size={18} className="text-yellow-400" />
                          {account.username}
                        </p>
                        <p className="text-xs text-slate-400 mt-2 font-mono break-all">{account.token}</p>
                      </div>
                      <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">{account.status}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3 text-xs text-yellow-300">
                      <div>🤖 {account.models} Models</div>
                      <div>📊 {account.datasets} Datasets</div>
                      <a href={account.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">View Profile →</a>
                    </div>
                    <p className="text-xs text-slate-400 mb-3">Last synced: {account.lastSync}</p>
                    <div className="flex gap-2">
                      <button className="flex-1 px-3 py-2 bg-blue-600/50 text-blue-300 rounded text-xs hover:bg-blue-600 font-semibold">Sync Models</button>
                      <button className="flex-1 px-3 py-2 bg-yellow-600/50 text-yellow-300 rounded text-xs hover:bg-yellow-600 font-semibold">Edit</button>
                      <button className="flex-1 px-3 py-2 bg-red-600/50 text-red-300 rounded text-xs hover:bg-red-600 font-semibold">Disconnect</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hugging Face Models Section */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <BrainCircuit size={20} />
                Published Models & Datasets
              </h3>

              <div className="space-y-3">
                {huggingFaceModels.map(model => (
                  <div key={model.id} className="bg-slate-800/50 rounded-lg p-4 border border-yellow-500/20 hover:border-yellow-500/40 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <a href={model.url} target="_blank" rel="noopener noreferrer" className="text-lg font-bold text-yellow-400 hover:text-yellow-300 flex items-center gap-2">
                          <BrainCircuit size={18} />
                          {model.owner}/{model.name}
                        </a>
                        <p className="text-sm text-slate-400 mt-1">{model.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">Task:</span>
                        <p className="text-slate-200 font-semibold">{model.task}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">Framework:</span>
                        <p className="text-slate-200 font-semibold">{model.framework}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">📥 Downloads:</span>
                        <p className="text-slate-200 font-semibold">{model.downloads.toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded px-3 py-2">
                        <span className="text-slate-400">❤️ Likes:</span>
                        <p className="text-slate-200 font-semibold">{model.likes}</p>
                      </div>
                    </div>

                    <p className="text-xs text-slate-400 mb-3">Last updated: {model.lastUpdate}</p>

                    <div className="flex gap-2">
                      <button className="flex-1 px-3 py-2 bg-blue-600/50 text-blue-300 rounded text-xs hover:bg-blue-600 font-semibold flex items-center justify-center gap-1">
                        <Eye size={14} />
                        View Model
                      </button>
                      <button className="flex-1 px-3 py-2 bg-yellow-600/50 text-yellow-300 rounded text-xs hover:bg-yellow-600 font-semibold flex items-center justify-center gap-1">
                        <GitBranch size={14} />
                        Use Model
                      </button>
                      <button className="flex-1 px-3 py-2 bg-green-600/50 text-green-300 rounded text-xs hover:bg-green-600 font-semibold flex items-center justify-center gap-1">
                        <Copy size={14} />
                        Copy Code
                      </button>
                      <button className="flex-1 px-3 py-2 bg-purple-600/50 text-purple-300 rounded text-xs hover:bg-purple-600 font-semibold">Deploy</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
